import {
  ColumnFactory,
  GraphQLError,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { TabStyles } from '@tableau/widgets';
import * as React from 'react';

import { createObfuscatedLinkOrTextCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { HeaderlessDisplayGrid } from './HeaderlessDisplayGrid';

export const rowHeight = TabStyles.Typography.Sizing.XLarge;

export function getRowHeight(rowCount: number | null, maxRows: number): number {
  return Math.min((rowCount || 1), maxRows) * rowHeight;
}

export interface LinksDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
}

export type LinksDisplayGridColumnProps = {
  readonly link: (urls: SiteUrls, rowData: LinksDisplayGridRowData) => string | null;
  readonly name: (rowData: LinksDisplayGridRowData) => string | null;
  targetBlank?: boolean;
  useAnchor?: boolean;
};

export interface LinksDisplayGridProps extends UsualDisplayGridProps<LinksDisplayGridRowData, GraphQLError> {
  link: (urls: SiteUrls, rowData: LinksDisplayGridRowData) => string | null;
  name: (rowData: LinksDisplayGridRowData) => string | null;
  targetBlank?: boolean;
  useAnchor?: boolean;
}

export const columns: ColumnFactory<LinksDisplayGridColumnProps, LinksDisplayGridRowData>[] = [
  ({ link, name, targetBlank, useAnchor }) => ({
    name: 'name',
    dataKey: UnsortableColumnKey,
    headerText: '',
    cellFormatter: createObfuscatedLinkOrTextCellFormatter(
      (rowData) => rowData.name === null,
      (rowData) => name(rowData),
      (urls, rowData) => link(urls, rowData),
      true,
      targetBlank,
      useAnchor
    ),
    sortable: false,
  })
];

export function LinksDisplayGrid(props: LinksDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory({
    link: props.link,
    name: props.name,
    targetBlank: props.targetBlank,
    useAnchor: props.useAnchor
  }));
  return (
    <HeaderlessDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      rowHeight={rowHeight}
      renderErrors={reportGraphQLErrors}
    />
  );
}
